import { SupabaseClient } from '@supabase/supabase-js';
import { supabase as supabaseClient } from '../client/browser';

export interface UserProfileData {
  id: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  phone?: string;
  timezone?: string;
  gender?: string;
  pronoun?: string;
  birthday?: string;
  unit?: string;
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  emergencyName?: string;
  emergencyPhone?: string;
  referrer?: string;
  referredBy?: string;
  referralCode?: string;
  partnershipId?: string;
  marketingOptIn?: boolean;
  smsOptIn?: boolean;
  newsletterOptIn?: boolean;
  marketingOptInDate?: string;
  marketingOptOutDate?: string;
  isPaidReferrer?: boolean;
  stripeCustomerId?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

/**
 * User service for all User table operations (clients, not therapists)
 */
export const userService = {
  /**
   * Get user by ID
   */
  async getById(userId: string, client?: SupabaseClient): Promise<UserProfileData | null> {
    const supabase = client || supabaseClient;
    
    console.log('[UserService.getById] Fetching user profile for:', userId);
    
    try {
      // First attempt - direct query without timeout wrapper
      // Sometimes the timeout wrapper itself causes issues
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('[UserService.getById] Error fetching user:', error);
        // If we get a specific error, we might want to retry or handle it differently
        if (error.message?.includes('JWSError') || error.message?.includes('JWT')) {
          console.log('[UserService.getById] Auth token issue detected, user may need to refresh');
        }
        return null;
      }
      
      if (!data) {
        console.log('[UserService.getById] No user found for userId:', userId);
        return null;
      }
      
      console.log('[UserService.getById] User fetched successfully:', {
        userId,
        hasTimezone: !!data?.timezone,
        timezone: data?.timezone,
        firstName: data?.firstName,
        email: data?.email
      });
      
      return data;
    } catch (err) {
      console.error('[UserService.getById] Unexpected error:', err);
      return null;
    }
  },

  /**
   * Get user by email
   */
  async getByEmail(email: string, client?: SupabaseClient): Promise<UserProfileData | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();
    
    if (error) {
      console.error('Error fetching user by email:', error);
      return null;
    }
    
    return data;
  },

  /**
   * Create or update a user record.
   *
   * Upserts on `id` because the handle_new_auth_user DB trigger already
   * inserts a stub public.users row when its matching auth.users row is
   * created. A plain insert here would PK-collide with that stub.
   */
  async create(userData: UserProfileData & { id?: string }, client?: SupabaseClient): Promise<{ id: string } | null> {
    const supabase = client || supabaseClient;

    const { data, error } = await supabase
      .from('users')
      .upsert(
        {
          ...userData,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        { onConflict: 'id' }
      )
      .select('id')
      .single();

    if (error) {
      console.error('Error creating user:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update user profile
   */
  async updateProfile(
    userId: string,
    profileData: UserProfileData,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;
    
    const updateData: any = {
      updatedAt: new Date()
    };
    
    // Only update fields that are provided
    if (profileData.firstName !== undefined) updateData.firstName = profileData.firstName;
    if (profileData.lastName !== undefined) updateData.lastName = profileData.lastName;
    if (profileData.preferredName !== undefined) updateData.preferredName = profileData.preferredName;
    if (profileData.email !== undefined) updateData.email = profileData.email;
    if (profileData.phone !== undefined) updateData.phone = profileData.phone;
    if (profileData.timezone !== undefined) updateData.timezone = profileData.timezone;
    if (profileData.gender !== undefined) updateData.gender = profileData.gender;
    if (profileData.pronoun !== undefined) updateData.pronoun = profileData.pronoun;
    if (profileData.birthday !== undefined) updateData.birthday = profileData.birthday;
    if (profileData.unit !== undefined) updateData.unit = profileData.unit;
    if (profileData.street !== undefined) updateData.street = profileData.street;
    if (profileData.city !== undefined) updateData.city = profileData.city;
    if (profileData.province !== undefined) updateData.province = profileData.province;
    if (profileData.country !== undefined) updateData.country = profileData.country;
    if (profileData.postalCode !== undefined) updateData.postalCode = profileData.postalCode;
    if (profileData.emergencyName !== undefined) updateData.emergencyName = profileData.emergencyName;
    if (profileData.emergencyPhone !== undefined) updateData.emergencyPhone = profileData.emergencyPhone;
    if (profileData.referrer !== undefined) updateData.referrer = profileData.referrer;
    if (profileData.referredBy !== undefined) updateData.referredBy = profileData.referredBy;
    if (profileData.referralCode !== undefined) updateData.referralCode = profileData.referralCode;
    if (profileData.partnershipId !== undefined) updateData.partnershipId = profileData.partnershipId;
    if (profileData.stripeCustomerId !== undefined) updateData.stripeCustomerId = profileData.stripeCustomerId;
    if (profileData.isPaidReferrer !== undefined) updateData.isPaidReferrer = profileData.isPaidReferrer;
    
    // Handle marketing opt-ins with date tracking
    if (profileData.marketingOptIn !== undefined) {
      updateData.marketingOptIn = profileData.marketingOptIn;
      if (profileData.marketingOptIn) {
        updateData.marketingOptInDate = new Date();
      } else {
        updateData.marketingOptOutDate = new Date();
      }
    }
    if (profileData.smsOptIn !== undefined) updateData.smsOptIn = profileData.smsOptIn;
    if (profileData.newsletterOptIn !== undefined) updateData.newsletterOptIn = profileData.newsletterOptIn;
    
    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  /**
   * Update or create a user (upsert)
   */
  async upsert(userData: UserProfileData & { id: string }, client?: SupabaseClient): Promise<{ id: string }> {
    const supabase = client || supabaseClient;
    
    const existing = await this.getById(userData.id, supabase);
    
    if (existing) {
      await this.updateProfile(userData.id, userData, supabase);
      return { id: userData.id };
    } else {
      const result = await this.create(userData, supabase);
      if (!result) throw new Error('Failed to create user');
      return result;
    }
  },

  /**
   * Update Stripe customer ID
   */
  async updateStripeCustomerId(
    userId: string,
    stripeCustomerId: string,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('users')
      .update({
        stripeCustomerId,
        updatedAt: new Date()
      })
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating Stripe customer ID:', error);
      throw error;
    }
  },

  /**
   * Update partnership association
   */
  async updatePartnership(
    userId: string,
    partnershipId: string | null,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('users')
      .update({
        partnershipId,
        updatedAt: new Date()
      })
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating partnership:', error);
      throw error;
    }
  }
};